import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import StashitLogo from '../components/Logo/StashitLogo';
import { API_ENDPOINTS } from '../config/api';

interface EscrowRequest {
  id: string;
  errand_id: string;
  user_id: string;
  courier_id?: string;
  admin_id?: string;
  amount: number;
  service_fee: number;
  courier_fee: number;
  total_amount: number;
  status: 'pending' | 'approved' | 'funded' | 'completed' | 'billed' | 'paid' | 'cancelled';
  admin_notes?: string;
  user_notes?: string;
  courier_notes?: string;
  admin_approved_at?: string;
  funds_sent_at?: string;
  errand_completed_at?: string;
  user_billed_at?: string;
  user_paid_at?: string;
  courier_paid_at?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  errand?: {
    title: string;
    description?: string;
    status: string;
  };
  user?: {
    full_name: string;
    phone: string;
    email: string;
  };
  courier?: {
    full_name: string;
    phone: string;
    email: string;
  };
}

const AdminEscrowManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [escrowRequests, setEscrowRequests] = useState<EscrowRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<EscrowRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'completed'>('pending');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchEscrowRequests();
  }, [user, navigate]);

  const fetchEscrowRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch escrow requests');
      }

      const data = await response.json();
      setEscrowRequests(data.escrowRequests || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch escrow requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests/${requestId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_notes: adminNotes })
      });

      if (!response.ok) {
        throw new Error('Failed to approve escrow request');
      }

      await fetchEscrowRequests();
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleFundRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests/${requestId}/fund`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fund escrow request');
      }

      await fetchEscrowRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fund request');
    } finally {
      setLoading(false);
    }
  };

  const handleBillUser = async (requestId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests/${requestId}/bill`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to bill user');
      }

      await fetchEscrowRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bill user');
    } finally {
      setLoading(false);
    }
  };

  const handlePayCourier = async (requestId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/admin/escrow-requests/${requestId}/pay-courier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to pay courier');
      }

      await fetchEscrowRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pay courier');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'funded': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'billed': return 'bg-orange-100 text-orange-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'funded': return '💰';
      case 'completed': return '🎯';
      case 'billed': return '📄';
      case 'paid': return '💳';
      case 'cancelled': return '❌';
      default: return '❓';
    }
  };

  const filteredRequests = escrowRequests.filter(request => {
    switch (activeTab) {
      case 'pending':
        return request.status === 'pending';
      case 'active':
        return ['approved', 'funded', 'completed', 'billed'].includes(request.status);
      case 'completed':
        return request.status === 'paid';
      default:
        return true;
    }
  });

  if (!user || user.role !== 'admin') {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Access Denied</h1>
          <p className="text-neutral-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <StashitLogo width={200} height={60} showTagline={false} />
        </div>
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          Admin Escrow Management
        </h1>
        <p className="text-neutral-600">
          Manage secure escrow transactions for financial errands
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 text-sm mt-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-neutral-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          ⏳ Pending Review ({escrowRequests.filter(r => r.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'active'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          🔄 Active ({escrowRequests.filter(r => ['approved', 'funded', 'completed', 'billed'].includes(r.status)).length})
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'completed'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          ✅ Completed ({escrowRequests.filter(r => r.status === 'paid').length})
        </button>
      </div>

      {/* Escrow Requests List */}
      <div className="space-y-4">
        {loading && escrowRequests.length === 0 ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-3 text-gray-300">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">No Escrow Requests</h3>
            <p className="text-neutral-600">
              {activeTab === 'pending' && 'No pending escrow requests to review'}
              {activeTab === 'active' && 'No active escrow transactions'}
              {activeTab === 'completed' && 'No completed escrow transactions'}
            </p>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <div key={request.id} className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800">
                    {request.errand?.title || 'Untitled Errand'}
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Request ID: {request.id.slice(0, 8)}...
                  </p>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)} {request.status.replace('_', ' ').toUpperCase()}
                  </div>
                  <div className="text-lg font-bold text-primary-700 mt-1">
                    P{request.total_amount.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <h4 className="font-medium text-neutral-700 mb-2">Customer</h4>
                  <p className="text-neutral-800">{request.user?.full_name || 'Unknown'}</p>
                  <p className="text-sm text-neutral-600">{request.user?.phone || 'No phone'}</p>
                </div>
                {request.courier && (
                  <div>
                    <h4 className="font-medium text-neutral-700 mb-2">Courier</h4>
                    <p className="text-neutral-800">{request.courier.full_name}</p>
                    <p className="text-sm text-neutral-600">{request.courier.phone}</p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-neutral-700 mb-2">Amount Breakdown</h4>
                  <p className="text-sm text-neutral-600">Base: P{request.amount.toFixed(2)}</p>
                  <p className="text-sm text-neutral-600">Service Fee: P{request.service_fee.toFixed(2)}</p>
                  <p className="text-sm text-neutral-600">Courier Fee: P{request.courier_fee.toFixed(2)}</p>
                </div>
              </div>

              {request.user_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <h4 className="font-medium text-blue-800 mb-1">Customer Notes</h4>
                  <p className="text-blue-700 text-sm">{request.user_notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {request.status === 'pending' && (
                  <>
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                    >
                      Review & Approve
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setAdminNotes('Request rejected');
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                    >
                      Reject
                    </button>
                  </>
                )}

                {request.status === 'approved' && (
                  <button
                    onClick={() => handleFundRequest(request.id)}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Send Funds to Courier'}
                  </button>
                )}

                {request.status === 'completed' && (
                  <button
                    onClick={() => handleBillUser(request.id)}
                    disabled={loading}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Send Bill to Customer'}
                  </button>
                )}

                {request.status === 'billed' && (
                  <button
                    onClick={() => handlePayCourier(request.id)}
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Pay Courier'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Approval Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Review Escrow Request
            </h3>

            <div className="mb-4">
              <h4 className="font-medium text-neutral-700 mb-2">Errand Details</h4>
              <p className="text-neutral-800">{selectedRequest.errand?.title}</p>
              <p className="text-sm text-neutral-600">Amount: P{selectedRequest.total_amount.toFixed(2)}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Admin Notes
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="Add notes about this request..."
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleApproveRequest(selectedRequest.id)}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Approve Request'}
              </button>
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setAdminNotes('');
                }}
                className="flex-1 bg-neutral-300 hover:bg-neutral-400 text-neutral-700 px-4 py-2 rounded-md font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEscrowManagement;
